import React from 'react';
import { Grid, Checkbox, Dropdown, Input, Popup, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import SectionLabel from '../../../components/SectionLabel';
import { FieldLabel } from '../../../styles';
import { EstateFormProps } from '../UpdateEstateForm';

const ConfigCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HDILogo = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #59249B 0%, #8B5CF6 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
`;

const ConfigContent = styled.div`
  flex: 1;
`;

const ConfigTitle = styled.h4`
  margin: 0 0 4px 0;
  color: #333;
`;

const ConfigDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
`;

const FieldsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ErrorText = styled.span`
  color: #db2828;
  font-size: 12px;
  margin-top: 2px;
`;

const rateOptions = [
  { key: '19.90', value: 19.90, text: 'R$ 19,90 (Padrão)' },
  { key: '15.90', value: 15.90, text: 'R$ 15,90' },
];

// Regra: para 15,90 max 30%, para 19,90 max 40%
const getMaxProlabore = (rate: number): number => {
  if (rate === 15.90) return 30;
  return 40; // 19.90 ou qualquer outro
};

export const LenderInsuranceConfig = (props: EstateFormProps) => {
  const { values, setFieldValue } = props;

  const currentRate = Number(values.lenderInsuranceRate) || 19.90;
  const maxProlabore = getMaxProlabore(currentRate);
  const currentProlabore = Number(values.lenderInsuranceProlaboreTax) || 0;
  const isProlaboreInvalid = currentProlabore > maxProlabore;

  const handleRateChange = (value: number) => {
    setFieldValue('lenderInsuranceRate', value);

    // Verificar se a taxa prolabore atual excede o novo máximo
    const newMax = getMaxProlabore(value);
    if (currentProlabore > newMax) {
      setFieldValue('lenderInsuranceProlaboreTax', newMax);
    }
  };

  const sanitizePercentageInput = (value: string) => {
    const normalizedValue = String(value ?? '')
      .replace(/,/g, '.')
      .replace(/[^\d.]/g, '');
    const firstDecimalSeparatorIndex = normalizedValue.indexOf('.');

    if (firstDecimalSeparatorIndex === -1) {
      return normalizedValue;
    }

    const integerPart = normalizedValue.slice(0, firstDecimalSeparatorIndex);
    const decimalPart = normalizedValue
      .slice(firstDecimalSeparatorIndex + 1)
      .replace(/\./g, '')
      .slice(0, 2);

    return `${integerPart}.${decimalPart}`;
  };

  const handleProlaboreChange = (value: string) => {
    setFieldValue('lenderInsuranceProlaboreTax', sanitizePercentageInput(value));
  };

  const handleProlaboreBlur = () => {
    const sanitizedValue = sanitizePercentageInput(String(values.lenderInsuranceProlaboreTax ?? ''));

    if (!sanitizedValue || sanitizedValue === '.') {
      setFieldValue('lenderInsuranceProlaboreTax', 0);
      return;
    }

    const parsed = Number(sanitizedValue);

    if (!Number.isFinite(parsed)) {
      setFieldValue('lenderInsuranceProlaboreTax', 0);
      return;
    }

    setFieldValue('lenderInsuranceProlaboreTax', Math.min(parsed, maxProlabore));
  };

  return (
    <>
      <SectionLabel text="SEGURO PRESTAMISTA" />
      <Grid.Row>
        <Grid.Column width={16}>
          <ConfigCard>
            <TopRow>
              <HDILogo>HDI</HDILogo>
              <ConfigContent>
                <ConfigTitle>Prestamista HDI Seguros</ConfigTitle>
                <ConfigDescription>
                  Habilite para permitir que esta imobiliária contrate Seguro Prestamista
                  durante os fluxos de Fiança, Incêndio e Capitalização.
                </ConfigDescription>
              </ConfigContent>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <FieldLabel style={{ display: 'block', marginBottom: '5px' }}>
                  {values.lenderInsuranceEnabled ? 'Habilitado' : 'Desabilitado'}
                </FieldLabel>
                <Checkbox
                  toggle
                  checked={values.lenderInsuranceEnabled}
                  onChange={(_, data) =>
                    setFieldValue('lenderInsuranceEnabled', data.checked)
                  }
                  style={{ marginTop: '5px' }}
                />
              </div>
            </TopRow>

            {values.lenderInsuranceEnabled && (
              <FieldsRow>
                <FieldWrapper>
                  <FieldLabel>Taxa Mensal</FieldLabel>
                  <Dropdown
                    selection
                    options={rateOptions}
                    value={currentRate}
                    onChange={(_, { value }) => handleRateChange(value as number)}
                    style={{ minWidth: 180 }}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <LabelWithTooltip>
                    <FieldLabel style={{ marginBottom: 0 }}>Taxa Prolabore</FieldLabel>
                    <Popup
                      trigger={<Icon name="info circle" color="grey" style={{ cursor: 'pointer' }} />}
                      content={
                        <div>
                          <p><strong>Regras de valor máximo:</strong></p>
                          <p>• Taxa R$ 15,90: máximo <strong>30%</strong></p>
                          <p>• Taxa R$ 19,90: máximo <strong>40%</strong></p>
                        </div>
                      }
                      position="top center"
                      size="small"
                    />
                  </LabelWithTooltip>
                  <Input
                    label="%"
                    labelPosition="right"
                    placeholder="0"
                    value={
                      values.lenderInsuranceProlaboreTax === 0
                        ? ''
                        : values.lenderInsuranceProlaboreTax ?? ''
                    }
                    onChange={(_, data) => handleProlaboreChange(data.value)}
                    onBlur={handleProlaboreBlur}
                    error={isProlaboreInvalid}
                    style={{ width: 140 }}
                    type="text"
                    inputMode="decimal"
                  />
                  {isProlaboreInvalid && (
                    <ErrorText>Máximo permitido: {maxProlabore}%</ErrorText>
                  )}
                  {!isProlaboreInvalid && currentProlabore > 0 && (
                    <span style={{ fontSize: '11px', color: '#888' }}>
                      Máx: {maxProlabore}%
                    </span>
                  )}
                </FieldWrapper>
              </FieldsRow>
            )}
          </ConfigCard>
        </Grid.Column>
      </Grid.Row>
    </>
  );
};

export default LenderInsuranceConfig;
